import { useCallback, useEffect, useRef, useState } from "react";
import type { AlterlyEffectInstance } from "../../core/effects/effectRegistry";
import type { MediaAsset } from "../../core/media/mediaTypes";
import { renderEffectStack } from "../../engine/cpu/renderEffectStack";
import { GpuEffectStackRenderer } from "../../engine/gpu/GpuEffectStackRenderer";

type PreviewStageProps = {
  asset: MediaAsset;
  onClear: () => void;
  effects: readonly AlterlyEffectInstance[];
};

type RenderMode = "gpu" | "cpu";

const MIN_ZOOM = 25;
const MAX_ZOOM = 300;
const ZOOM_STEP = 25;
const NO_EFFECTS: readonly AlterlyEffectInstance[] = [];

function formatBytes(bytes: number) {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDuration(seconds?: number) {
  if (typeof seconds !== "number" || !Number.isFinite(seconds)) return null;
  const minutes = Math.floor(seconds / 60);
  const remaining = Math.floor(seconds % 60);
  return `${minutes}:${remaining.toString().padStart(2, "0")}`;
}

function clampZoom(value: number) {
  return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, value));
}

export function PreviewStage({ asset, onClear, effects }: PreviewStageProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const gpuRendererRef = useRef<GpuEffectStackRenderer | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const fallbackFrameRef = useRef<number | null>(null);
  const [renderMode, setRenderMode] = useState<RenderMode>("gpu");
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [zoom, setZoom] = useState(100);
  const [showOriginal, setShowOriginal] = useState(false);
  const duration = formatDuration(asset.duration);
  const visibleEffects = showOriginal ? NO_EFFECTS : effects;

  const scheduleCpuFallback = useCallback(() => {
    if (fallbackFrameRef.current !== null) return;
    fallbackFrameRef.current = requestAnimationFrame(() => {
      fallbackFrameRef.current = null;
      setRenderMode("cpu");
    });
  }, []);

  useEffect(() => {
    if (renderMode !== "gpu") return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    try {
      gpuRendererRef.current = new GpuEffectStackRenderer(canvas);
    } catch {
      gpuRendererRef.current = null;
      scheduleCpuFallback();
      return;
    }

    return () => {
      gpuRendererRef.current?.dispose();
      gpuRendererRef.current = null;
    };
  }, [renderMode, scheduleCpuFallback]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const previewScale = Math.min(1, 1200 / Math.max(asset.dimensions.width, asset.dimensions.height));
    const width = Math.max(1, Math.round(asset.dimensions.width * previewScale));
    const height = Math.max(1, Math.round(asset.dimensions.height * previewScale));

    const renderSource = (source: CanvasImageSource) => {
      if (renderMode === "gpu") {
        const renderer = gpuRendererRef.current;
        if (!renderer) return;
        renderer.resize(width, height);
        const rendered = renderer.render(source, visibleEffects);
        if (!rendered) scheduleCpuFallback();
        return;
      }

      const context = canvas.getContext("2d");
      if (!context) return;
      if (canvas.width !== width) canvas.width = width;
      if (canvas.height !== height) canvas.height = height;
      renderEffectStack(context, source, width, height, visibleEffects);
    };

    if (asset.kind === "image") {
      const image = imageRef.current;
      if (!image) return;
      const render = () => renderSource(image);
      if (image.complete) render();
      else image.addEventListener("load", render, { once: true });
      return () => image.removeEventListener("load", render);
    }

    const video = videoRef.current;
    if (!video) return;
    const renderVideo = () => {
      if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) renderSource(video);
      if (!video.paused && !video.ended) animationFrameRef.current = requestAnimationFrame(renderVideo);
    };

    renderVideo();
    const handleSeek = () => renderVideo();
    video.addEventListener("seeked", handleSeek);
    video.addEventListener("loadeddata", handleSeek);

    return () => {
      video.removeEventListener("seeked", handleSeek);
      video.removeEventListener("loadeddata", handleSeek);
      if (animationFrameRef.current !== null) cancelAnimationFrame(animationFrameRef.current);
    };
  }, [asset, visibleEffects, renderMode, scheduleCpuFallback]);

  useEffect(() => () => {
    if (fallbackFrameRef.current !== null) cancelAnimationFrame(fallbackFrameRef.current);
  }, []);

  const togglePlayback = async () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) await video.play();
    else video.pause();
  };

  const changeZoom = (delta: number) => {
    setZoom((current) => clampZoom(current + delta));
  };

  return (
    <div className="preview-stage">
      <div className="preview-stage__toolbar">
        <div className="preview-stage__meta">
          <strong title={asset.name}>{asset.name}</strong>
          <span>
            {asset.dimensions.width} × {asset.dimensions.height} · {formatBytes(asset.size)}
            {duration ? ` · ${duration}` : ""} · {renderMode === "gpu" ? "GPU" : "CPU"}
          </span>
        </div>

        <div className="preview-stage__tools" aria-label="Canvas controls">
          <div className="preview-stage__zoom" aria-label="Zoom controls">
            <button type="button" onClick={() => changeZoom(-ZOOM_STEP)} disabled={zoom <= MIN_ZOOM} aria-label="Zoom out">−</button>
            <button type="button" onClick={() => setZoom(100)} title="Fit canvas">{zoom === 100 ? "Fit" : `${zoom}%`}</button>
            <button type="button" onClick={() => changeZoom(ZOOM_STEP)} disabled={zoom >= MAX_ZOOM} aria-label="Zoom in">+</button>
          </div>
          <button
            type="button"
            className={`preview-stage__compare${showOriginal ? " is-active" : ""}`}
            aria-pressed={showOriginal}
            onPointerDown={() => setShowOriginal(true)}
            onPointerUp={() => setShowOriginal(false)}
            onPointerLeave={() => setShowOriginal(false)}
            onKeyDown={(event) => {
              if (event.key === " " || event.key === "Enter") setShowOriginal(true);
            }}
            onKeyUp={() => setShowOriginal(false)}
          >
            Hold original
          </button>
          <button type="button" onClick={onClear}>Replace</button>
        </div>
      </div>

      <div className="preview-stage__viewport">
        <div className="preview-stage__media-wrap">
          <canvas
            key={renderMode}
            ref={canvasRef}
            className="preview-stage__media"
            style={{ transform: `scale(${zoom / 100})` }}
            aria-label={`${asset.name} preview`}
          />
          {asset.kind === "image" ? (
            <img ref={imageRef} className="preview-stage__source" src={asset.objectUrl} alt="" draggable={false} />
          ) : (
            <video
              ref={videoRef}
              className="preview-stage__source"
              src={asset.objectUrl}
              playsInline
              preload="metadata"
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
            />
          )}
        </div>
      </div>

      {asset.kind === "video" && (
        <div className="preview-stage__video-controls">
          <button type="button" onClick={() => { void togglePlayback(); }}>{playing ? "Pause" : "Play"}</button>
          <input
            type="range"
            min="0"
            max={asset.duration ?? 0}
            step="0.01"
            value={currentTime}
            onChange={(event) => {
              const video = videoRef.current;
              if (!video) return;
              const time = Number(event.target.value);
              video.currentTime = time;
              setCurrentTime(time);
            }}
            aria-label="Video position"
          />
          <span>{formatDuration(currentTime) ?? "0:00"} / {duration ?? "0:00"}</span>
        </div>
      )}
    </div>
  );
}
